// ============================================================================
// Group Event Service
// Manages event templates, instances, site assignments, and releases
// ============================================================================

import {
  EventTemplate,
  EventTemplateCreate,
  EventTemplateUpdate,
  EventInstance,
  EventInstanceCreate,
  EventInstanceUpdate,
  EventTemplateSite,
  EventTemplateSiteBulkCreate,
  EventInstanceSite,
  EventInstanceSiteBulkCreate,
  EventInstanceSiteUpdate,
  EventSiteRelease,
} from '../types';
import { fetchWithAuth } from './auth';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8004/api';

// ============================================================================
// Templates
// ============================================================================

export async function getTemplates(storeId?: number, activeOnly: boolean = true): Promise<EventTemplate[]> {
  const params = new URLSearchParams();
  if (storeId) params.append('store_id', storeId.toString());
  params.append('active_only', activeOnly.toString());
  const queryString = params.toString();
  return fetchWithAuth(`${API_BASE_URL}/groupevent-bookings/templates${queryString ? '?' + queryString : ''}`);
}

export async function getTemplate(templateId: number): Promise<EventTemplate> {
  return fetchWithAuth(`${API_BASE_URL}/groupevent-bookings/templates/${templateId}`);
}

export async function createTemplate(data: EventTemplateCreate): Promise<EventTemplate> {
  return fetchWithAuth(`${API_BASE_URL}/groupevent-bookings/templates`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateTemplate(templateId: number, data: EventTemplateUpdate): Promise<EventTemplate> {
  return fetchWithAuth(`${API_BASE_URL}/groupevent-bookings/templates/${templateId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteTemplate(templateId: number): Promise<void> {
  return fetchWithAuth(`${API_BASE_URL}/groupevent-bookings/templates/${templateId}`, {
    method: 'DELETE',
  });
}

// ============================================================================
// Template Sites
// ============================================================================

export async function getTemplateSites(templateId: number, activeOnly: boolean = true): Promise<EventTemplateSite[]> {
  const params = new URLSearchParams();
  params.append('active_only', activeOnly.toString());
  return fetchWithAuth(`${API_BASE_URL}/groupevent-bookings/templates/${templateId}/sites?${params}`);
}

export async function addTemplateSites(templateId: number, data: EventTemplateSiteBulkCreate): Promise<EventTemplateSite[]> {
  return fetchWithAuth(`${API_BASE_URL}/groupevent-bookings/templates/${templateId}/sites`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function removeTemplateSite(templateId: number, siteId: number): Promise<void> {
  return fetchWithAuth(`${API_BASE_URL}/groupevent-bookings/templates/${templateId}/sites/${siteId}`, {
    method: 'DELETE',
  });
}

// ============================================================================
// Instances
// ============================================================================

export async function getInstances(
  storeId?: number,
  templateId?: number,
  activeOnly: boolean = true,
  includePast: boolean = false
): Promise<EventInstance[]> {
  const params = new URLSearchParams();
  if (storeId) params.append('store_id', storeId.toString());
  if (templateId) params.append('template_id', templateId.toString());
  params.append('active_only', activeOnly.toString());
  params.append('include_past', includePast.toString());
  const queryString = params.toString();
  return fetchWithAuth(`${API_BASE_URL}/groupevent-bookings/instances${queryString ? '?' + queryString : ''}`);
}

export async function getInstance(instanceId: number): Promise<EventInstance> {
  return fetchWithAuth(`${API_BASE_URL}/groupevent-bookings/instances/${instanceId}`);
}

export async function createInstance(data: EventInstanceCreate): Promise<EventInstance> {
  return fetchWithAuth(`${API_BASE_URL}/groupevent-bookings/instances`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateInstance(instanceId: number, data: EventInstanceUpdate): Promise<EventInstance> {
  return fetchWithAuth(`${API_BASE_URL}/groupevent-bookings/instances/${instanceId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteInstance(instanceId: number): Promise<void> {
  return fetchWithAuth(`${API_BASE_URL}/groupevent-bookings/instances/${instanceId}`, {
    method: 'DELETE',
  });
}

// ============================================================================
// Instance Sites
// ============================================================================

export async function getInstanceSites(instanceId: number, activeOnly: boolean = true): Promise<EventInstanceSite[]> {
  const params = new URLSearchParams();
  params.append('active_only', activeOnly.toString());
  return fetchWithAuth(`${API_BASE_URL}/groupevent-bookings/instances/${instanceId}/sites?${params}`);
}

export async function addInstanceSites(instanceId: number, data: EventInstanceSiteBulkCreate): Promise<EventInstanceSite[]> {
  return fetchWithAuth(`${API_BASE_URL}/groupevent-bookings/instances/${instanceId}/sites`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateInstanceSite(instanceId: number, siteId: number, data: EventInstanceSiteUpdate): Promise<EventInstanceSite> {
  return fetchWithAuth(`${API_BASE_URL}/groupevent-bookings/instances/${instanceId}/sites/${siteId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function removeInstanceSite(instanceId: number, siteId: number): Promise<void> {
  return fetchWithAuth(`${API_BASE_URL}/groupevent-bookings/instances/${instanceId}/sites/${siteId}`, {
    method: 'DELETE',
  });
}

// ============================================================================
// Site Releases
// ============================================================================

export async function getInstanceReleases(instanceId: number, releasedOnly: boolean = false): Promise<EventSiteRelease[]> {
  const params = new URLSearchParams();
  params.append('released_only', releasedOnly.toString());
  return fetchWithAuth(`${API_BASE_URL}/groupevent-bookings/instances/${instanceId}/releases?${params}`);
}

export async function releaseSite(instanceId: number, siteId: number): Promise<EventSiteRelease> {
  return fetchWithAuth(`${API_BASE_URL}/groupevent-bookings/instances/${instanceId}/sites/${siteId}/release`, {
    method: 'POST',
  });
}

export async function unreleaseSite(instanceId: number, siteId: number): Promise<EventSiteRelease> {
  return fetchWithAuth(`${API_BASE_URL}/groupevent-bookings/instances/${instanceId}/sites/${siteId}/unrelease`, {
    method: 'POST',
  });
}

// ============================================================================
// Available Sites
// ============================================================================

export interface AvailableSiteInfo {
  id: number;
  name: string;
  site_number?: string;
  alias: string;
  description?: string;
  type_id?: number;
  site_type_name?: string;
  site_section_id?: number;
  base_price?: number;
}

export async function getAvailableSitesForInstance(instanceId: number): Promise<AvailableSiteInfo[]> {
  return fetchWithAuth(`${API_BASE_URL}/groupevent-bookings/instances/${instanceId}/available-sites`);
}

// ============================================================================
// Instance Bookings
// ============================================================================

export async function getInstanceBookings(instanceId: number, statusFilter?: string): Promise<any[]> {
  const params = new URLSearchParams();
  if (statusFilter) params.append('status_filter', statusFilter);
  const queryString = params.toString();
  return fetchWithAuth(`${API_BASE_URL}/groupevent-bookings/instances/${instanceId}/bookings${queryString ? '?' + queryString : ''}`);
}

// ============================================================================
// Named Export Object
// ============================================================================

export const groupEventService = {
  // Templates
  getTemplates,
  getTemplate,
  createTemplate,
  updateTemplate,
  deleteTemplate,

  // Template Sites
  getTemplateSites,
  addTemplateSites,
  removeTemplateSite,

  // Instances
  getInstances,
  getInstance,
  createInstance,
  updateInstance,
  deleteInstance,

  // Instance Sites
  getInstanceSites,
  addInstanceSites,
  updateInstanceSite,
  removeInstanceSite,

  // Site Releases
  getInstanceReleases,
  releaseSite,
  unreleaseSite,

  // Available Sites
  getAvailableSitesForInstance,

  // Instance Bookings
  getInstanceBookings,
};

export default groupEventService;
